import cloneDeep from 'lodash/cloneDeep';
import {
  WIRInterventionOperationSections,
  WIRPhaseSectionApiKeys,
  WIRStageGate4Sections,
} from 'pages/WellGuardian/WIR/wir.constants';
import { useCallback, useMemo } from 'react';
import wellGuardianApiClient from 'services/networking/wellGuardianApiClient';
import {
  WIR,
  WIREquipmentIdentification,
  WIRInitialRequestInformation,
  WIRManagerialSummary,
  WIROrganiseKickOffMeeting,
  WIRSection,
  WIRSectionDTO,
  WIRWellInformation,
} from 'types/wellGuardian';
import { useCountryState, useSelectedWIR } from '../../../../redux/WellGuardian/selectors';

import {
  getInterventionOperationPhaseName,
  getInterventionOperationSectionKey,
  getInterventionOperationWellHandOver,
} from '../utils/getSectionData';

const getIsTicked = (
  subphase:
    | WIRSection
    | WIRInitialRequestInformation
    | WIROrganiseKickOffMeeting
    | WIRWellInformation
    | WIREquipmentIdentification
    | WIRManagerialSummary
    | undefined,
) => {
  if (subphase?.isRequired) {
    return subphase?.attachments && subphase?.attachments?.length > 0;
  } else {
    return subphase?.comments && subphase?.comments?.length > 0;
  }
};

export const useWellHandOverChecklistData = () => {
  const wirFromState = useSelectedWIR();
  const wir = useMemo(() => cloneDeep(wirFromState), [wirFromState]);
  const countryState = useCountryState();
  const phase = getInterventionOperationSectionKey(countryState);

  const wirSections = useMemo(() => wir?.phases?.[phase]?.sections, [phase, wir?.phases]);

  const wirWellHandOver = useMemo(
    () => wir?.phases?.[phase]?.sections?.wellHandover,
    [phase, wir?.phases],
  );

  const getChecklistData = useCallback(() => {
    const checklistObj: WIRSectionDTO[] = [];
    for (const sec in wirSections) {
      if (getInterventionOperationWellHandOver(countryState).includes(sec)) {
        const subphase = wirSections[sec as keyof typeof wirSections];

        const isTicked = getIsTicked(subphase);

        checklistObj.push({
          ...subphase,
          section: WIRPhaseSectionApiKeys[sec as keyof typeof WIRPhaseSectionApiKeys],
          phase: getInterventionOperationPhaseName(countryState),
          isTicked: isTicked,
          sectionCompleteConfirmation: isTicked,
        });
      }
    }

    return checklistObj;
  }, [countryState, wirSections]);

  const wellHandOverSectionPayload = useMemo(() => {
    const CHECKLIST_DATA = getChecklistData();

    const isTickedAll = CHECKLIST_DATA.find(checklist => !checklist.isTicked);

    const wirChecklist: WIRSectionDTO = {
      ...wirWellHandOver,
      section: 'WELL_HANDOVER',
      phase: getInterventionOperationPhaseName(countryState),
      isTicked: isTickedAll === undefined,
    };
    CHECKLIST_DATA.push(wirChecklist);

    return [...CHECKLIST_DATA];
  }, [countryState, getChecklistData, wirWellHandOver]);

  const saveWellHandOverChecklistSection = useCallback(
    async (section: string, processSaveResponse: (wirResponse: WIR | null) => void) => {
      const phaseName = getInterventionOperationPhaseName(countryState);
      if (
        wirFromState?.wirNumber &&
        (section === WIRInterventionOperationSections.wellHandover ||
          section === WIRStageGate4Sections.wellHandover)
      ) {
        processSaveResponse(
          await wellGuardianApiClient.updateWIRWellHandoverChecklistSection(
            wirFromState?.wirNumber,
            wellHandOverSectionPayload,
            phaseName,
            countryState,
          ),
        );
      }
    },
    [wirFromState, wellHandOverSectionPayload, countryState],
  );

  return {
    saveWellHandOverChecklistSection,
    wellHandOverSectionPayload,
  };
};
